import { authFetch } from '../utils/authFetch';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000') + '/api/v1';

// Integration Interfaces
export interface XeroDirectIntegration {
  id: string;
  organization_id: string;
  access_token_enc: string;
  refresh_token_enc: string;
  id_token_enc?: string;
  scope?: string;
  token_type?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface XeroDirectTenant {
  id: string;
  organization_id: string;
  xero_connection_id: string;
  xero_tenant_id: string;
  tenant_type?: string;
  tenant_name?: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface XeroIntegrationData {
  integration: XeroDirectIntegration;
  tenants: XeroDirectTenant[];
}

// Sync Interfaces
export interface XeroSyncResult {
  status: string;
  message: string;
  sync_type: string;
  results: {
    contacts: SyncTableResult;
    invoices: SyncTableResult;
    payments: SyncTableResult;
    credit_notes: SyncTableResult;
    accounts: SyncTableResult;
    items: SyncTableResult;
    tracking_categories: SyncTableResult;
  };
}

export interface SyncTableResult {
  status: string;
  created?: number;
  updated?: number;
  total?: number;
  error?: string;
}

export interface XeroSyncStatus {
  id: string;
  tenant_id: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  sync_summary?: any;
  error_message?: string;
}

export interface XeroSyncHistory {
  status: string;
  organization_id: string;
  tenant_id?: string;
  sync_history: XeroSyncStatus[];
  total_count: number;
}

export interface XeroLatestSync {
  status: string;
  has_synced: boolean;
  message?: string;
  last_sync?: {
    id: string;
    sync_type: string;
    completed_at: string;
    duration_seconds: number;
    sync_summary: any;
  };
}

export interface XeroInitialSyncResult {
  status: string;
  message: string;
  sync_type: string;
  results: {
    contacts: SyncTableResult;
    invoices: SyncTableResult;
    payments: SyncTableResult;
    credit_notes: SyncTableResult;
    accounts: SyncTableResult;
    items: SyncTableResult;
    tracking_categories: SyncTableResult;
  };
}

export interface XeroInitialSyncStatus {
  id: string;
  organization_id: string;
  tenant_id: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  progress?: {
    [key: string]: {
      processed: number;
      total: number;
      status: string;
    };
  };
  current_table?: string;
  overall_progress?: number;
  started_at_iso?: string;
  estimated_completion?: string;
  final_results?: any;
  total_duration_seconds?: number;
  error_message?: string;
}

export interface XeroLatestInitialSync {
  status: string;
  has_initial_synced: boolean;
  message?: string;
  last_initial_sync?: {
    id: string;
    status: string;
    started_at: string;
    completed_at?: string;
    duration_seconds?: number;
    overall_progress?: number;
  };
}

export const xeroService = {
  // ===== INTEGRATION OPERATIONS =====
  
  // Get Xero integration details by integration ID
  async getIntegrationDetails(
    integrationId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroIntegrationData> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/integration/${integrationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch integration details');
    }

    return response.json();
  },

  // Get Xero integration details by organization ID
  async getIntegrationByOrganization(
    organizationId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroIntegrationData> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/integration/organization/${organizationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch integration details');
    }

    return response.json();
  },



  // ===== SYNC OPERATIONS =====

  // Start an incremental sync
  async startIncrementalSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroSyncResult> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: organizationId,
        tenant_id: tenantId,
        sync_type: 'incremental'
      }),
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start incremental sync');
    }

    return response.json();
  },

  // Start a full sync
  async startFullSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroSyncResult> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/full`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: organizationId,
        tenant_id: tenantId
      }),
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start full sync');
    }

    return response.json();
  },

  // Start initial sync
  async startInitialSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void,
    syncOptions?: any
  ): Promise<XeroInitialSyncResult> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/initial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organization_id: organizationId,
        tenant_id: tenantId,
        sync_options: syncOptions
      }),
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to start initial sync');
    }

    return response.json();
  },

  // Get sync status and history
  async getSyncStatus(
    organizationId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void,
    tenantId?: string,
    limit: number = 10
  ): Promise<XeroSyncHistory> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      limit: limit.toString()
    });
    
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/status?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch sync status');
    }

    return response.json();
  },

  // Get latest sync information
  async getLatestSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroLatestSync> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      tenant_id: tenantId
    });

    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/latest?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to fetch latest sync');
    }

    return response.json();
  },

  // Get initial sync status
  async getInitialSyncStatus(
    syncId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroInitialSyncStatus> {
    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/initial/status/${syncId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get initial sync status');
    }

    return response.json();
  },

  // Get latest initial sync
  async getLatestInitialSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<XeroLatestInitialSync> {
    const params = new URLSearchParams({
      organization_id: organizationId,
      tenant_id: tenantId
    });

    const response = await authFetch(`${API_BASE_URL}/xero_direct/sync/initial/latest?${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }, accessToken, setUserAndToken);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get latest initial sync');
    }

    return response.json();
  },

  // Check if sync is in progress
  async isSyncInProgress(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<boolean> {
    try {
      const syncHistory = await this.getSyncStatus(organizationId, accessToken, setUserAndToken, tenantId, 1);
      
      if (syncHistory.sync_history.length === 0) {
        return false;
      }

      const latestSync = syncHistory.sync_history[0];
      return latestSync.status === 'in_progress';
    } catch (error) {
      console.error('Error checking sync status:', error);
      return false;
    }
  },

  // Check if initial sync is needed
  async needsInitialSync(
    organizationId: string,
    tenantId: string,
    accessToken: string,
    setUserAndToken: (user: any, token: string) => void
  ): Promise<boolean> {
    try {
      const latestInitialSync = await this.getLatestInitialSync(organizationId, tenantId, accessToken, setUserAndToken);
      return !latestInitialSync.has_initial_synced;
    } catch (error) {
      console.error('Error checking if initial sync is needed:', error);
      return true; // Assume initial sync is needed if we can't determine
    }
  },

  // ===== UTILITY METHODS =====

  // Format sync duration for display
  formatSyncDuration(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  },

  // Format sync results for display
  formatSyncResults(results: any): string {
    const parts = [];
    
    if (results.contacts?.total) {
      parts.push(`${results.contacts.total} contacts`);
    }
    if (results.invoices?.total) {
      parts.push(`${results.invoices.total} invoices`);
    }
    if (results.payments?.total) {
      parts.push(`${results.payments.total} payments`);
    }
    if (results.credit_notes?.total) {
      parts.push(`${results.credit_notes.total} credit notes`);
    }
    if (results.accounts?.total) {
      parts.push(`${results.accounts.total} accounts`);
    }
    if (results.items?.total) {
      parts.push(`${results.items.total} items`);
    }
    if (results.tracking_categories?.total) {
      parts.push(`${results.tracking_categories.total} tracking categories`);
    }

    return parts.join(', ') || 'No data synced';
  },

  // Format initial sync progress for display
  formatInitialSyncProgress(progress: any): string {
    if (!progress) return 'No progress data';
    
    const parts = [];
    for (const [tableName, tableProgress] of Object.entries(progress)) {
      const progressData = tableProgress as { status: string; total: number; processed: number };
      if (progressData.status === 'completed') {
        parts.push(`${tableName}: ${progressData.total} records`);
      } else if (progressData.status === 'in_progress') {
        parts.push(`${tableName}: ${progressData.processed}/${progressData.total}`);
      }
    }
    
    return parts.join(', ') || 'No progress data';
  },

  // Get overall progress percentage
  getOverallProgress(progress: any): number {
    if (!progress) return 0;
    
    const totalTables = Object.keys(progress).length;
    const completedTables = Object.values(progress).filter((p: any) => (p as any).status === 'completed').length;
    
    if (totalTables === 0) return 0;
    
    // Calculate progress based on completed tables and current table progress
    let totalProgress = completedTables;
    
    for (const tableProgress of Object.values(progress)) {
      const progressData = tableProgress as { status: string; total: number; processed: number };
      if (progressData.status === 'in_progress' && progressData.total > 0) {
        totalProgress += (progressData.processed / progressData.total);
      }
    }
    
    return Math.round((totalProgress / totalTables) * 100);
  }
};
